import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';
import { UtilityService } from 'src/app/services/utility.service';
import { SessionService } from 'src/app/services/session.service';
import { FeatureFlagService } from 'src/app/services/feature-flag.service';
import { ActiveHospitalService } from 'src/app/services/active-hospital.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-auth-callback',
  templateUrl: './auth-callback.component.html',
  styleUrls: ['./auth-callback.component.scss'],
  standalone: false
})
export class AuthCallbackComponent implements OnInit {

  error: string | null = null;
  processing: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: ApiService,
    private utility: UtilityService,
    private sessionService: SessionService,
    private featureFlagService: FeatureFlagService,
    private activeHospitalService: ActiveHospitalService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const samlCode = params['saml_code'];
      const code = params['code'];
      const state = params['state'];
      const error = params['error'];
      const errorDescription = params['error_description'];

      if (error) {
        this.handleError(error, errorDescription);
        return;
      }

      // SAML flow: exchange temporary code for token
      if (samlCode) {
        this.handleSamlCallback(samlCode);
        return;
      }

      // OAuth2 flow (legacy): exchange authorization code
      if (code && state) {
        this.handleCallback(code, state);
        return;
      }

      this.handleError('MISSING_PARAMS', 'Missing authentication parameters');
    });
  }

  handleSamlCallback(samlCode: string): void {
    this.utility.setSpinner(true);
    this.processing = true;

    this.api.apiRequest('get', `auth/saml/token?code=${samlCode}`, {})
      .subscribe(
        (result: any) => {
          this.utility.setSpinner(false);
          if (result.status === 'success') {
            this.processSuccessfulLogin(result.data);
          } else {
            this.handleError(result.error || 'LOGIN_FAILED', result.message);
          }
        },
        (error) => {
          this.utility.setSpinner(false);
          this.handleError('API_ERROR', 'Failed to complete authentication');
          console.error('SAML callback error:', error);
        }
      );
  }

  handleCallback(code: string, state: string): void {
    this.utility.setSpinner(true);
    this.processing = true;

    // Call backend to exchange code for user data
    this.api.apiRequest('post', 'auth/entra/callback', { code, state })
      .subscribe(
        (result: any) => {
          this.utility.setSpinner(false);

          // Check if backend returned an error status
          if (result.status === 'error') {
            // Backend returned error (e.g., INVALID_SESSION)
            this.handleError(result.error || 'LOGIN_FAILED', result.message);
          } else if (result.status === 'success') {
            this.processSuccessfulLogin(result.data);
          } else {
            this.handleError('LOGIN_FAILED', result.message || 'Authentication failed');
          }
        },
        (error) => {
          this.utility.setSpinner(false);
          this.handleError('API_ERROR', 'Failed to complete authentication');
          console.error('Entra callback error:', error);
        }
      );
  }

  processSuccessfulLogin(data: any): void {
    const userData = data;

    // Store auth data in localStorage (matches existing pattern)
    localStorage.setItem('endUserEmail', userData.email);
    localStorage.setItem('endUserId', userData._id);
    localStorage.setItem('endUserType', userData.userType);
    localStorage.setItem('endUserFullName', `${userData.firstName} ${userData.lastName}`);
    localStorage.setItem('endUserFirstName', userData.firstName);
    localStorage.setItem('endUserLastName', userData.lastName);
    localStorage.setItem('accessToken', userData.accessToken);
    localStorage.setItem('contactNumber', userData.contactNumber || '');
    localStorage.setItem('status', userData.status);

    if (userData.profilePic) {
      localStorage.setItem('profilePic', `${environment.documentBaseUrl}profilePic/${userData.profilePic}`);
    }

    // Store hospital-related data for VC users (specialty intake only)
    if (this.featureFlagService.isSpecialtyIntakeEnabled()) {
      if (userData.hospitalCode) {
        localStorage.setItem('hospitalCode', userData.hospitalCode);
      }
      if (userData.hospitalRole) {
        localStorage.setItem('hospitalRole', userData.hospitalRole);
      }
      if (userData.staffRole) {
        localStorage.setItem('staffRole', userData.staffRole);
      }
      if (userData.practiceName) {
        localStorage.setItem('practiceName', userData.practiceName);
      }
      // Multi-site: initialize accessible hospitals
      if (userData.accessibleHospitals) {
        this.activeHospitalService.initialize(userData.accessibleHospitals);
      }
    }

    // Start session monitoring for auto-logout
    this.sessionService.startSession(false);

    // Navigate to appropriate dashboard — primary site preselected, user changes via navbar
    this.navigateToDashboard(userData.userType, userData.status);
  }

  navigateToDashboard(userType: string, status: string): void {
    let route: string;

    switch (userType) {
      case 'specialist':
        route = status === 'active' ? '/specialist/dashboard/day2' : '/specialist/dashboard';
        break;
      case 'vc':
      case 'pcv':
        route = status === 'active' ? '/pcv/dashboard/day2' : '/pcv/my-vc-profile';
        break;
      case 'admin':
        route = '/admin/dashboard';
        break;
      default:
        route = '/authentication/login';
    }

    this.router.navigate([route]);
  }

  handleError(error: string, description?: string): void {
    this.processing = false;
    this.error = description || error;

    console.error('[EntraSSO] Authentication error:', error, description);
    this.router.navigate(['/'], {
      queryParams: { error: error }
    });
  }
}
